"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.evaluateScan = evaluateScan;
exports.useBarcodeScanner = useBarcodeScanner;
const react_1 = require("react");
const browser_1 = require("@zxing/browser");
const library_1 = require("@zxing/library");
const trackingNumber_1 = require("./trackingNumber");
/** Ten sam kod w kadrze dekoduje się kilka razy na sekundę — bez tego lista by się zapchała. */
const REPEAT_WINDOW_MS = 1500;
/**
 * Cała decyzja o pojedynczym odczycie, jako funkcja czysta — dzięki temu da się ją
 * przetestować bez kamery i bez Reacta. Hook tylko ją stosuje.
 */
function evaluateScan(raw, known, previous, now) {
    if (previous && previous.value === raw && now - previous.at < REPEAT_WINDOW_MS)
        return { action: "ignore" };
    const rejection = (0, trackingNumber_1.describeRejection)(raw, known);
    if (rejection)
        return { action: "reject", rejection };
    return { action: "accept", trackingNumber: (0, trackingNumber_1.normalizeTrackingNumber)(raw) };
}
/**
 * Zamknięcie kamery.
 *
 * Samo `controls.stop()` nie wystarcza: biblioteka próbuje jeszcze zmienić ustawienia
 * strumienia, a jeżeli ścieżka jest już zamykana, Chromium odrzuca to błędem
 * `setPhotoOptions failed`, który wypływa jako nieobsłużony wyjątek. Dlatego zamykamy
 * po kolei i sami: najpierw dekoder, potem ścieżki strumienia, na końcu odpinamy obraz.
 * Każdy krok osobno, bo blad jednego nie moze zablokować pozostałych.
 */
function stopCamera(controls, video) {
    try {
        controls?.stop();
    }
    catch {
        // biblioteka bywa hałaśliwa przy zamykaniu — zatrzymanie i tak następuje niżej
    }
    const stream = video?.srcObject;
    stream?.getTracks().forEach((track) => {
        try {
            track.stop();
        }
        catch {
            // ścieżka mogła już zostać zamknięta przez bibliotekę
        }
    });
    if (video) {
        try {
            video.srcObject = null;
        }
        catch {
            // nieistotne przy odmontowaniu komponentu
        }
    }
}
/**
 * Czyta numery nadania z kamery. Kamera jest wygodą, nie warunkiem pracy: pole numeru
 * w formularzu zostaje zwykłym polem tekstowym, więc odmowa dostępu albo słaba kamera
 * niczego nie blokują.
 *
 * Ograniczamy dekoder do Code 128, bo tylko taki kod jest na potwierdzeniu nadania —
 * to szybsze i mniej podatne na przypadkowy odczyt czegoś innego z tła.
 */
function useBarcodeScanner({ active, knownNumbers, onAccepted, }) {
    const videoRef = (0, react_1.useRef)(null);
    const controlsRef = (0, react_1.useRef)(null);
    const previousRef = (0, react_1.useRef)(null);
    const knownRef = (0, react_1.useRef)(knownNumbers);
    const onAcceptedRef = (0, react_1.useRef)(onAccepted);
    const [status, setStatus] = (0, react_1.useState)("idle");
    const [rejection, setRejection] = (0, react_1.useState)(null);
    const [acceptedCount, setAcceptedCount] = (0, react_1.useState)(0);
    // Świeże wartości bez restartu kamery — restart gubiłby ostrość i denerwował.
    (0, react_1.useEffect)(() => {
        knownRef.current = knownNumbers;
    }, [knownNumbers]);
    (0, react_1.useEffect)(() => {
        onAcceptedRef.current = onAccepted;
    }, [onAccepted]);
    const handleResult = (0, react_1.useCallback)((raw) => {
        const decision = evaluateScan(raw, knownRef.current, previousRef.current, Date.now());
        previousRef.current = { value: raw, at: Date.now() };
        if (decision.action === "ignore")
            return;
        if (decision.action === "reject") {
            setRejection(decision.rejection);
            return;
        }
        setRejection(null);
        setAcceptedCount((count) => count + 1);
        onAcceptedRef.current(decision.trackingNumber);
    }, []);
    (0, react_1.useEffect)(() => {
        if (!active)
            return;
        if (!navigator.mediaDevices?.getUserMedia) {
            setStatus("unavailable");
            return;
        }
        let cancelled = false;
        setStatus("starting");
        setRejection(null);
        previousRef.current = null;
        const hints = new Map();
        hints.set(library_1.DecodeHintType.POSSIBLE_FORMATS, [library_1.BarcodeFormat.CODE_128]);
        hints.set(library_1.DecodeHintType.TRY_HARDER, true);
        const reader = new browser_1.BrowserMultiFormatReader(hints, {
            delayBetweenScanAttempts: 100,
            delayBetweenScanSuccess: 300,
        });
        reader
            .decodeFromConstraints({ video: { facingMode: "environment" } }, videoRef.current ?? undefined, (result) => {
            if (result)
                handleResult(result.getText());
        })
            .then((controls) => {
            if (cancelled) {
                controls.stop();
                return;
            }
            controlsRef.current = controls;
            setStatus("scanning");
        })
            .catch((error) => {
            if (cancelled)
                return;
            setStatus(error?.name === "NotAllowedError" ? "denied" : "error");
        });
        return () => {
            cancelled = true;
            stopCamera(controlsRef.current, videoRef.current);
            controlsRef.current = null;
            setStatus("idle");
        };
    }, [active, handleResult]);
    return { videoRef, status, rejection, acceptedCount };
}
